/**
 * Field-level validators for the connector wizard.
 *
 * `runValidators` runs the implicit required check (when `field.required`)
 * followed by the field's explicit validator list. First error wins. Returns
 * `null` when the field is valid.
 */

import type {
  ConnectorFieldConfig,
  ConnectorFieldValidator,
} from "./ConnectorWizardModal.types";

const DEFAULT_MESSAGES: Record<ConnectorFieldValidator["kind"], string> = {
  required: "This field is required",
  minLength: "Value is too short",
  maxLength: "Value is too long",
  pattern: "Value does not match the expected format",
  url: "Enter a valid URL",
  custom: "Invalid value",
};

const isEmpty = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "");

const runRule = (
  rule: ConnectorFieldValidator,
  value: unknown,
  allValues: Record<string, unknown>,
): string | null => {
  switch (rule.kind) {
    case "required":
      return isEmpty(value) ? rule.message ?? DEFAULT_MESSAGES.required : null;

    case "minLength":
      if (typeof value !== "string" || value.length >= rule.value) return null;
      return rule.message ?? `Must be at least ${rule.value} characters`;

    case "maxLength":
      if (typeof value !== "string" || value.length <= rule.value) return null;
      return rule.message ?? `Must be at most ${rule.value} characters`;

    case "pattern":
      if (typeof value !== "string" || rule.value.test(value)) return null;
      return rule.message ?? DEFAULT_MESSAGES.pattern;

    case "url": {
      if (typeof value !== "string" || value === "") return null;
      try {
        // eslint-disable-next-line no-new
        new URL(value);
        return null;
      } catch {
        return rule.message ?? DEFAULT_MESSAGES.url;
      }
    }

    case "custom":
      return rule.validate(value, allValues) ?? null;

    default:
      return null;
  }
};

/**
 * Validate a single field. Implicit `required` runs first when the field is
 * marked required, then explicit validators in declaration order.
 */
export const validateField = (
  field: ConnectorFieldConfig,
  value: unknown,
  allValues: Record<string, unknown>,
): string | null => {
  if (field.required && isEmpty(value)) {
    return DEFAULT_MESSAGES.required;
  }
  // Skip non-required validators when the field is empty — empty optionals
  // are valid by definition, only required fields can fail on emptiness.
  if (isEmpty(value)) return null;

  for (const rule of field.validators ?? []) {
    const error = runRule(rule, value, allValues);
    if (error) return error;
  }
  return null;
};

/**
 * Validate every visible field in a list and return a {key: error} map. Hidden
 * fields (failing `visibleWhen`) are skipped — they shouldn't block submit.
 */
export const validateFields = (
  fields: ConnectorFieldConfig[],
  values: Record<string, unknown>,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (field.visibleWhen && !field.visibleWhen(values)) continue;
    const error = validateField(field, values[field.key], values);
    if (error) errors[field.key] = error;
  }
  return errors;
};
